import { IKey } from '@/common/constants/ikey'

/** Scalar text from a UniData field entry or plain string/number. */
export function unwrapLabelFieldScalar(field: unknown): string {
  if (field == null) return ''
  if (typeof field === 'string' || typeof field === 'number' || typeof field === 'boolean') {
    return String(field)
  }
  if (typeof field === 'object' && field !== null && 'value' in field) {
    const v = (field as { value?: unknown }).value
    if (v == null || v === '') return ''
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      return String(v)
    }
    if (typeof v === 'object' && v !== null && 'value' in v) {
      return unwrapLabelFieldScalar(v)
    }
    return ''
  }
  return ''
}

/**
 * GlobalProvider.refillElement passes `{ [elementName]: typedFieldData }`, not a bare field.
 */
export function resolveLabelRefillText(data: unknown, elementName?: string): string {
  if (data == null) return ''
  let field: unknown = data
  if (typeof data === 'object' && !Array.isArray(data)) {
    const map = data as Record<string, unknown>
    const name = String(elementName ?? '').trim()
    if (name && Object.prototype.hasOwnProperty.call(map, name)) {
      field = map[name]
    } else if (!('type' in map && 'value' in map)) {
      const keys = Object.keys(map)
      if (keys.length === 1) field = map[keys[0]]
    }
  }
  return unwrapLabelFieldScalar(field)
}

/** Unique registry key when several LABEL elements share the same `name`. */
export function labelRegistryKey(name: string, elementId: string | number): string {
  const n = String(name ?? '').trim()
  const id = String(elementId ?? '').trim()
  if (n && id) return `${n}#${id}`
  return n || id
}

export function resolveLabelDisplayText(options: {
  labelName?: string
  elementId?: string | number | null
  description?: string
  pageFields?: Record<string, unknown>
  override?: string | null
}): string {
  const override = options.override
  if (override != null && override !== '') {
    return override
  }

  const labelName = String(options.labelName ?? '').trim()
  const elementId =
    options.elementId != null && String(options.elementId).trim() !== ''
      ? String(options.elementId).trim()
      : ''
  const pf = options.pageFields

  if (pf && elementId) {
    const scopedKeys = [
      ...(labelName ? [`${labelName}${IKey.LABEL_DATA}`] : []),
      `${elementId}${IKey.LABEL_DATA}`,
      ...(labelName ? [`${labelName}__${elementId}`, `${labelName}@${elementId}`] : []),
    ]
    for (const key of scopedKeys) {
      const fromPage = unwrapLabelFieldScalar(pf[key])
      if (fromPage) return fromPage
    }
  } else if (pf && labelName) {
    for (const key of [`${labelName}${IKey.LABEL_DATA}`, labelName]) {
      const fromPage = unwrapLabelFieldScalar(pf[key])
      if (fromPage) return fromPage
    }
  }

  return String(options.description ?? '').trim()
}
